import { useState } from 'react';
import figmaLogo from './assets/figma.svg';
import linearLogo from './assets/linear.svg';
import notionLogo from './assets/notion.svg';
import { Cursor, Stage, after } from './Stage';
import styles from './bring.module.css';

// The work behind the Duolingo case study, found by searching for it.
const QUERY = 'sign-up';

type SourceId = 'figma' | 'notion' | 'folder' | 'linear';
const SOURCES: { id: SourceId; name: string; logo?: string }[] = [
  { id: 'figma', name: 'Duolingo sign-up flow', logo: figmaLogo },
  { id: 'notion', name: 'Sign-up teardown notes', logo: notionLogo },
  { id: 'folder', name: 'duolingo-sign-up' },
  { id: 'linear', name: 'Sign-up experiments', logo: linearLogo },
];

/**
 * The cursor shows up by the search and clicks into it (steps 1 to 3), the search types itself one letter at
 * a time, and the results land. Then the cursor does the picking: it ticks Figma (travel, press, tick) and
 * Notion, rests on the folder so its preview opens and ticks it, presses Read, and goes.
 */
const TYPED = QUERY.length;
const FOCUSED = 3;
const END = FOCUSED + TYPED;
const MARKS = [
  400,
  1250,
  1400,
  ...Array.from({ length: TYPED }, (_, i) => 1600 + i * 70),
  2450,
  2700,
  3650,
  3800,
  4100,
  4750,
  4900,
  5200,
  5850,
  6000,
  6400,
  7350,
  7500,
  8500,
];
const SHOWN = END + 1;
const PREVIEW = END + 8;
const TICKED_AT: Record<SourceId, number> = {
  figma: END + 4,
  notion: END + 7,
  folder: END + 10,
  linear: Infinity,
};

/** Where the cursor goes at each step of the tour, with its tip on the tick, then on Read. */
const TICK_AT = (row: number): [number, number] => [288, 208 + row * 42];
const READ_AT: [number, number] = [413, 362];
// The cursor comes in just below the search and clicks into it, left of the middle.
const NEAR_SEARCH_AT: [number, number] = [240, 205];
const SEARCH_AT: [number, number] = [193, 155];
function cursorFor(step: number): [number, number] {
  if (step >= END + 11) return READ_AT;
  if (step >= END + 8) return TICK_AT(2);
  if (step >= END + 5) return TICK_AT(1);
  if (step >= END + 2) return TICK_AT(0);
  if (step >= 1) return SEARCH_AT;
  return NEAR_SEARCH_AT;
}
const PRESSED = new Set([2, END + 3, END + 6, END + 9, END + 12]);
const TO_READ = END + 11;
const GONE = MARKS.length;
const ORDER = SOURCES.map((source) => source.id);

const scriptedTicks = (step: number) => ORDER.filter((id) => step >= TICKED_AT[id]);

export function BringScene({ label }: { label: string }) {
  // Untouched, the scene ticks what the script ticks; the first click hands the ticks to the person.
  const [ticked, setTicked] = useState<SourceId[] | null>(null);
  const [hovered, setHovered] = useState<SourceId | null>(null);

  return (
    <Stage label={label} marks={MARKS} className={styles.scene} onPointerLeave={() => setHovered(null)}>
      {(step) => {
        const picks = ticked ?? scriptedTicks(step);
        const lit =
          hovered ??
          (ticked
            ? null
            : step >= TO_READ
              ? null
              : step >= PREVIEW
                ? 'folder'
                : step >= END + 5
                  ? 'notion'
                  : step >= END + 2
                    ? 'figma'
                    : null);
        // The drawn cursor goes once the tour is over, or as soon as the person takes over.
        const showCursor = step >= 1 && step < GONE && !ticked;
        const toggle = (id: SourceId) => {
          const now = ticked ?? scriptedTicks(step);
          setTicked(now.includes(id) ? now.filter((p) => p !== id) : ORDER.filter((s) => s === id || now.includes(s)));
        };
        return (
          <>
            <div className={`${styles.preview} ${step >= PREVIEW || hovered === 'folder' ? styles.open : ''}`}>
              <div className={styles.caption}>
                <span>duolingo-sign-up</span>
                <span className={styles.count}>18 screens</span>
              </div>
              <div className={styles.wells}>
                {SHOTS.map((Shot, i) => (
                  <span key={i} className={styles.well} style={after(120 + i * 70)}>
                    <span className={styles.shot}>
                      <Shot />
                    </span>
                  </span>
                ))}
              </div>
            </div>

            <div className={styles.picker}>
              <div className={styles.search}>
                <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M11 2C15.971 2 20 6.029 20 11C20 13.125 19.262 15.077 18.03 16.616L21.707 20.293C22.098 20.683 22.098 21.317 21.707 21.707C21.317 22.098 20.683 22.098 20.293 21.707L16.616 18.03C15.077 19.262 13.125 20 11 20C6.029 20 2 15.971 2 11C2 6.029 6.029 2 11 2ZM11 4C7.134 4 4 7.134 4 11C4 14.866 7.134 18 11 18C14.866 18 18 14.866 18 11C18 7.134 14.866 4 11 4Z"
                    fill="rgb(41 41 41 / 45%)"
                  />
                </svg>
                <span className={styles.query}>
                  {QUERY.slice(0, Math.max(0, Math.min(step - FOCUSED, TYPED)))}
                  {step >= FOCUSED ? <span className={styles.caret} /> : null}
                </span>
              </div>
              <div className={`${styles.list} ${step >= SHOWN ? styles.found : ''}`}>
                {SOURCES.map((source, i) => {
                  const on = picks.includes(source.id);
                  return (
                    <button
                      key={source.id}
                      type="button"
                      className={`${styles.row} ${lit === source.id ? styles.lit : ''} ${on ? styles.on : ''}`}
                      style={after(i * 60)}
                      aria-pressed={on}
                      onClick={() => toggle(source.id)}
                      onPointerEnter={() => setHovered(source.id)}
                      onPointerLeave={() => setHovered(null)}
                    >
                      <span className={styles.logo}>
                        {source.logo ? <img src={source.logo} alt="" width={16} height={16} /> : <Folder size={17} />}
                      </span>
                      <span className={styles.name}>{source.name}</span>
                      <span className={styles.tick} aria-hidden>
                        <span className={styles.ring} />
                        <span className={styles.check}>
                          <svg width="9" height="9" viewBox="0 0 12 12">
                            <path
                              d="M2.5 6.2l2.4 2.4 4.6-5"
                              fill="none"
                              stroke="#FFFFFF"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={`${styles.actions} ${step >= TICKED_AT.figma || ticked ? styles.ready : ''}`}>
              <span className={styles.stack}>
                {picks.map((id) => {
                  const source = SOURCES.find((s) => s.id === id);
                  return (
                    <span key={id} className={styles.chip}>
                      {source?.logo ? <img src={source.logo} alt="" width={12} height={12} /> : <Folder size={13} />}
                    </span>
                  );
                })}
              </span>
              <span
                className={`${styles.read} ${picks.length === 0 ? styles.idle : ''} ${!ticked && step === END + 12 ? styles.pushed : ''}`}
              >
                {picks.length ? `Read ${picks.length}` : 'Read'}
                <span className={styles.kbd}>⌘↵</span>
              </span>
            </div>

            <Cursor at={cursorFor(step)} shown={showCursor} pressed={!ticked && PRESSED.has(step)} />
          </>
        );
      }}
    </Stage>
  );
}

/*
 * Four of the folder's Duolingo sign-up screens, drawn rather than screenshotted, in the Color system's palette:
 * as phones: why you're learning (Duo replies), the first question, the perfect lesson, and the ask to create a
 * profile, which runs off the card's edge so the folder reads as holding more.
 */
// The Color system's palette: blue, pink and tan are the brand (300), their 100 steps are fills, and sage is
// one of the supporting hues, kept for Duo.
const BLUE = '#84cce8';
const BLUE_SOFT = '#d2effb';
const PINK = '#f4b3d3';
const PINK_SOFT = '#fee2ef';
const TAN = '#d7bc95';
const TAN_SOFT = '#f5e7d4';
const SAGE = '#b3c9a7';

type Box = { x: number; y: number; w: number; h: number };
const at = ({ x, y, w, h }: Box) => ({ left: x, top: y, width: w, height: h });

function Bar({ x, y, w, h = 3, tone = 'faint' }: Omit<Box, 'h'> & { h?: number; tone?: 'ink' | 'faint' }) {
  return <i className={`${styles.bar} ${tone === 'ink' ? styles.barInk : ''}`} style={at({ x, y, w, h })} />;
}

/** Each phone screen is 60 by 128 with a 7px margin; answers stack, and the main button runs full width at the bottom. */
const M = 7;
const FULL = 60 - M * 2;
const BOTTOM = 112;

/** Duo, the one who talks: a soft face in the palette's sage, never Duolingo's own green. */
function Duo({ x, y, size = 12 }: { x: number; y: number; size?: number }) {
  return <i className={styles.dot} style={{ ...at({ x, y, w: size, h: size }), background: SAGE, boxShadow: '0 0 0 1.5px #fff' }} />;
}

/** Why are you learning: you pick Travel, and Duo answers. */
function WhyShot() {
  return (
    <>
      <Duo x={M} y={14} />
      <i className={styles.bubble} style={at({ x: 22, y: 14, w: 31, h: 12 })}>
        <i className={styles.bar} style={{ position: 'static', width: 19, height: 2.5 }} />
      </i>
      {[BLUE, PINK, TAN, SAGE].map((tone, i) => (
        <i key={tone} className={`${styles.option} ${i === 1 ? styles.optionOn : ''}`} style={at({ x: M, y: 36 + i * 13, w: FULL, h: 10 })}>
          <i className={styles.dot} style={{ width: 5, height: 5, background: tone }} />
          <i className={styles.bar} style={{ position: 'static', width: [22, 18, 26, 16][i], height: 2.5 }} />
        </i>
      ))}
      <i className={styles.button} style={at({ x: M, y: BOTTOM, w: FULL, h: 8 })} />
    </>
  );
}

/** The first question, made to be answered right: four pictures, the cat chosen. */
function QuestionShot() {
  const tiles = [TAN, BLUE, PINK, SAGE];
  return (
    <>
      <Bar x={M} y={15} w={32} h={3.5} tone="ink" />
      {tiles.map((tone, i) => (
        <i
          key={tone}
          className={`${styles.picture} ${i === 0 ? styles.pictureOn : ''}`}
          style={at({ x: M + (i % 2) * 24, y: 28 + Math.floor(i / 2) * 30, w: 22, h: 27 })}
        >
          <i className={styles.dot} style={{ width: 10, height: 10, borderRadius: 3, background: tone }} />
        </i>
      ))}
      <i className={styles.button} style={{ ...at({ x: M, y: BOTTOM, w: FULL, h: 8 }), background: BLUE }} />
    </>
  );
}

/** Lesson complete: a full ring for the perfect score, a little confetti, and what the lesson earned. */
function RewardShot() {
  const earned = [
    [TAN, TAN_SOFT],
    [BLUE, BLUE_SOFT],
    [PINK, PINK_SOFT],
  ];
  return (
    <>
      <i className={styles.score} style={{ ...at({ x: 16, y: 16, w: 28, h: 28 }), borderColor: BLUE }}>
        <i className={styles.dot} style={{ width: 9, height: 9, background: TAN }} />
      </i>
      <i className={styles.dot} style={{ ...at({ x: 10, y: 18, w: 3, h: 3 }), background: PINK }} />
      <i className={styles.dot} style={{ ...at({ x: 47, y: 21, w: 4, h: 4 }), background: TAN }} />
      <i className={styles.dot} style={{ ...at({ x: 45, y: 41, w: 3, h: 3 }), background: SAGE }} />
      <Bar x={12} y={54} w={36} h={3.5} tone="ink" />
      <Bar x={17} y={61} w={26} />
      {earned.map(([tone, soft], i) => (
        <i
          key={tone}
          className={styles.stat}
          style={{ ...at({ x: M + i * 16, y: 74, w: 14, h: 20 }), background: soft, boxShadow: `${tone} 0 0 0 1px` }}
        >
          <i className={styles.bar} style={{ position: 'static', width: 7, height: 2.5, background: '#292929cc' }} />
        </i>
      ))}
      <i className={styles.button} style={at({ x: M, y: BOTTOM, w: FULL, h: 8 })} />
    </>
  );
}

/** Time to create a profile: Duo, what you won so far, the ask, and Later as big as the button. */
function ProfileShot() {
  return (
    <>
      <Duo x={20} y={16} size={20} />
      <i className={styles.stat} style={{ ...at({ x: 10, y: 44, w: 18, h: 11 }), background: TAN_SOFT }}>
        <i className={styles.dot} style={{ width: 4, height: 4, background: TAN }} />
      </i>
      <i className={styles.stat} style={{ ...at({ x: 32, y: 44, w: 18, h: 11 }), background: BLUE_SOFT }}>
        <i className={styles.dot} style={{ width: 4, height: 4, background: BLUE }} />
      </i>
      <Bar x={11} y={66} w={38} h={3.5} tone="ink" />
      <Bar x={8} y={73} w={44} />
      <Bar x={14} y={79} w={32} />
      <i className={styles.button} style={at({ x: M, y: 99, w: FULL, h: 8 })} />
      <i className={styles.later} style={at({ x: M, y: BOTTOM, w: FULL, h: 8 })} />
    </>
  );
}

const SHOTS = [WhyShot, QuestionShot, RewardShot, ProfileShot];

function Folder({ size }: { size: number }) {
  return (
    <svg width={size} height={Math.round((size * 14) / 16)} viewBox="0 0 16 14" aria-hidden>
      <path
        d="M1 2.5A1.5 1.5 0 012.5 1h3.4l1.6 1.8h6A1.5 1.5 0 0115 4.3v7.2A1.5 1.5 0 0113.5 13h-11A1.5 1.5 0 011 11.5v-9z"
        fill="#E4DFD7"
        stroke="#292929"
        strokeWidth="1.2"
      />
    </svg>
  );
}
